import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUser, FaLock, FaKey, FaUsers, FaSave, FaUserPlus, FaTrash,
  FaEnvelope, FaPhone, FaIdBadge, FaShieldAlt,
} from 'react-icons/fa';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import { useToast } from '../../context/ToastContext';

const TABS = [
  { id: 'profile', label: 'My Profile', icon: FaUser },
  { id: 'security', label: 'Security', icon: FaLock },
  { id: 'admins', label: 'Admin Users', icon: FaUsers },
];

const AdminProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [limits, setLimits] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', profileImage: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [adminIdForm, setAdminIdForm] = useState({ currentPassword: '', newAdminID: '' });
  const [createForm, setCreateForm] = useState({ adminID: '', password: '', name: '', email: '', phone: '' });
  const toast = useToast();

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  const token = () => localStorage.getItem('adminToken');
  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token()}`,
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/admin-profile/me`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load profile');
      const data = await res.json();
      setProfile(data.profile);
      setLimits(data.limits);
      setIsAuthorized(data.isAuthorized);
      setProfileForm({
        name: data.profile?.name || '',
        email: data.profile?.email || '',
        phone: data.profile?.phone || '',
        profileImage: data.profile?.profileImage || '',
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [API, toast]);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/admin-users`, { headers: headers() });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to load admin users');
      }
      const data = await res.json();
      setAdmins(data.admins || []);
      setLimits(data.limits);
    } catch (err) {
      toast.error(err.message);
    }
  }, [API, toast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (activeTab === 'admins' && isAuthorized) fetchAdmins();
  }, [activeTab, isAuthorized, fetchAdmins]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/admin-profile/me`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed');
      setProfile(data.profile);
      if (data.profile?.name) localStorage.setItem('adminName', data.profile.name);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/admin-profile/change-password`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Password change failed');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdminIdChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/admin-profile/change-admin-id`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(adminIdForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Admin ID change failed');
      localStorage.setItem('adminID', data.newAdminID);
      if (data.profile?.name) localStorage.setItem('adminName', data.profile.name);
      setProfile(data.profile);
      setAdminIdForm({ currentPassword: '', newAdminID: '' });
      toast.success('Admin ID changed successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/admin-users`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(createForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create admin');
      setCreateForm({ adminID: '', password: '', name: '', email: '', phone: '' });
      setLimits(data.limits);
      fetchAdmins();
      toast.success('Admin user created successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Remove this admin user?')) return;
    try {
      const res = await fetch(`${API}/api/auth/admin-users/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Delete failed');
      setLimits(data.limits);
      fetchAdmins();
      toast.success('Admin user removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <SkeletonLoader variant="page" />;

  const initials = (profile?.name || profile?.adminID || 'A')[0]?.toUpperCase();

  return (
    <PageLayout>
      <PageHeader
        title="Admin Profile"
        description="Manage your personal profile, security settings, and admin accounts"
        icon={FaUser}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          if (tab.id === 'admins' && !isAuthorized) return null;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                isActive
                  ? 'btn-premium !py-2.5 border-transparent'
                  : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 text-center shadow-card">
              <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-3xl font-bold overflow-hidden">
                {profileForm.profileImage ? (
                  <img src={profileForm.profileImage} alt={profile?.name} className="w-full h-full object-cover" />
                ) : initials}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                {profile?.name || profile?.adminID}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{profile?.adminID}</p>
              {isAuthorized && (
                <span className="inline-flex items-center gap-1 mt-2 badge badge-success text-[10px]">
                  <FaShieldAlt className="w-3 h-3" /> Authorized Admin
                </span>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={handleProfileSave} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 shadow-card space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Display Name</label>
                  <div className="relative mt-1">
                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input className="input-modern pl-10" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} placeholder="Your name" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Admin ID (Login)</label>
                  <div className="relative mt-1">
                    <FaIdBadge className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input className="input-modern pl-10 bg-gray-50 dark:bg-dark-700" value={profile?.adminID || ''} disabled />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Email</label>
                  <div className="relative mt-1">
                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input type="email" className="input-modern pl-10" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} placeholder="contact@example.com" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Phone</label>
                  <div className="relative mt-1">
                    <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input className="input-modern pl-10" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} placeholder="+1 234 567 8900" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Profile Image URL</label>
                  <input className="input-modern mt-1" value={profileForm.profileImage} onChange={(e) => setProfileForm({ ...profileForm, profileImage: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <button type="submit" disabled={saving} className="btn-premium inline-flex items-center gap-2 !py-2.5">
                <FaSave className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <form onSubmit={handlePasswordChange} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaLock className="text-primary-500" /> Change Password
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your current password to update immediately. No email approval required while logged in.
            </p>
            <input type="password" className="input-modern" placeholder="Current Password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} required />
            <input type="password" className="input-modern" placeholder="New Password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} required />
            <input type="password" className="input-modern" placeholder="Confirm New Password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} required />
            <button type="submit" disabled={saving} className="btn-premium w-full !py-2.5">Update Password</button>
          </form>

          <form onSubmit={handleAdminIdChange} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaKey className="text-primary-500" /> Change Admin ID
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your new Admin ID must be unique. Changes apply immediately after verifying your current password.
            </p>
            <input type="password" className="input-modern" placeholder="Current Password" value={adminIdForm.currentPassword} onChange={(e) => setAdminIdForm({ ...adminIdForm, currentPassword: e.target.value })} required />
            <input type="text" className="input-modern" placeholder="New Admin ID" value={adminIdForm.newAdminID} onChange={(e) => setAdminIdForm({ ...adminIdForm, newAdminID: e.target.value })} required />
            <button type="submit" disabled={saving} className="btn-premium w-full !py-2.5">Update Admin ID</button>
          </form>
        </div>
      )}

      {activeTab === 'admins' && isAuthorized && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Admin Users</h3>
                <p className="text-sm text-gray-500">
                  {limits?.remainingSlots ?? 0} slot(s) remaining of {limits?.maxAdmins} maximum
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-dark-700 text-left text-xs uppercase text-gray-500">
                    <th className="py-2 pr-3">Admin ID</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {admins.map((admin) => (
                    <tr key={admin._id}>
                      <td className="py-2 pr-3 font-medium">{admin.adminID}</td>
                      <td className="py-2 pr-3">{admin.name || '—'}</td>
                      <td className="py-2 pr-3">{admin.email || '—'}</td>
                      <td className="py-2 pr-3">{admin.phone || '—'}</td>
                      <td className="py-2 text-right">
                        {admin._id !== profile?._id && (
                          <button type="button" onClick={() => handleDeleteAdmin(admin._id)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {(limits?.remainingSlots ?? 0) > 0 && (
            <form onSubmit={handleCreateAdmin} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-6 shadow-card space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FaUserPlus className="text-primary-500" /> Create Admin User
              </h3>
              <p className="text-sm text-gray-500">Admin ID must be one of: {limits?.authorizedAdminIds?.join(', ')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select className="input-modern" value={createForm.adminID} onChange={(e) => setCreateForm({ ...createForm, adminID: e.target.value })} required>
                  <option value="">Select Admin ID</option>
                  {limits?.authorizedAdminIds
                    ?.filter((id) => !admins.some((a) => a.adminID?.toLowerCase() === id.toLowerCase()))
                    .map((id) => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                </select>
                <input type="password" className="input-modern" placeholder="Password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required />
                <input className="input-modern" placeholder="Display Name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                <input type="email" className="input-modern" placeholder="Email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                <input className="input-modern" placeholder="Phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
              </div>
              <button type="submit" disabled={saving} className="btn-premium inline-flex items-center gap-2 !py-2.5">
                <FaUserPlus className="w-4 h-4" />
                {saving ? 'Creating...' : 'Create Admin'}
              </button>
            </form>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default AdminProfile;
